import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { logAnalyticsEvent } from '@/lib/referral';

/**
 * Inline conversion CTA for logged-out visitors.
 *
 * Variants:
 *   - "case"  — full card with both CTAs, sized for case-detail pages.
 *   - "match" — single-line text + button, sized to sit under a match
 *     preview card.
 *   - "hero"  — compact pair of buttons designed for the homepage hero.
 *
 * Caller is responsible for only rendering this when `user` is null.
 */
export function SignupCTA({ variant = 'case', context = 'generic', ctaTestId = 'signup-cta' }) {
  // Best-effort telemetry: we record which variant + context fired so we
  // can compare conversion lift across surfaces.
  const _track = (button) => {
    try {
      sessionStorage.setItem('cta_origin', `${context}:${button}`);
    } catch (_e) { /* ignore */ }
    // Fire-and-forget — never blocks the navigation.
    logAnalyticsEvent('cta_click', { variant, context, button });
  };

  if (variant === 'hero') {
    return (
      <div className="flex flex-col items-center gap-3 mt-3" data-testid={ctaTestId}>
        <p className="text-sm md:text-base text-white/90 max-w-2xl text-center" data-testid="hero-subtext">
          Search 143,000+ cases, see ranked candidate matches, and add research notes. Free forever — no credit card needed.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-4">
          <Link to="/register" className="w-full sm:w-auto" onClick={() => _track('hero-create')}>
            <Button
              size="lg"
              className="w-full sm:w-auto bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 md:px-10 py-5 md:py-6 font-bold shadow-lg transition-all text-sm md:text-base"
              data-testid="hero-create-account-btn"
            >
              <UserPlus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Start Searching Free
            </Button>
          </Link>
          <Link to="/pricing?trial=1" className="w-full sm:w-auto" onClick={() => _track('hero-trial')}>
            <Button
              size="lg"
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8 md:px-10 py-5 md:py-6 font-bold shadow-lg shadow-purple-600/30 transition-all text-sm md:text-base"
              data-testid="hero-trial-btn"
            >
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Start 7-Day Pro Trial
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (variant === 'match') {
    return (
      <div
        className="mt-3 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-xs sm:text-sm flex flex-wrap items-center justify-between gap-2"
        data-testid={ctaTestId}
      >
        <span className="text-purple-800 dark:text-purple-300">
          Create a free account to see all 10 candidate matches and add your research notes to this case.
        </span>
        <Link to="/register" onClick={() => _track('match-preview')}>
          <Button
            size="sm"
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs"
            data-testid="match-preview-cta-btn"
          >
            Join Free — Takes 30 Seconds
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </div>
    );
  }

  // Default: full card for case-detail page
  return (
    <Card
      className="border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 via-white to-orange-50 dark:from-purple-950/30 dark:via-slate-900 dark:to-orange-950/20"
      data-testid={ctaTestId}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="hidden sm:flex flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/40 items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
              Want to help solve this case?
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 sm:mb-4">
              Create a free account to see all 10 candidate matches, add research notes,
              and flag leads for law enforcement review.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Link to="/register" className="w-full sm:w-auto" onClick={() => _track('case-create')}>
                <Button
                  className="w-full sm:w-auto bg-safety-orange-500 hover:bg-safety-orange-600 text-white rounded-full font-semibold"
                  data-testid="case-create-account-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Free Account
                </Button>
              </Link>
              <Link to="/pricing?trial=1" className="w-full sm:w-auto" onClick={() => _track('case-trial')}>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto rounded-full font-semibold border-2 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                  data-testid="case-trial-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                  Start 7-Day Pro Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default SignupCTA;
